import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { fwHttpClientUtils } from '@myapp/modules/fwcore/models';
import { RequestDelete, ResponseDelete } from '@myapp/modules/fwcore/models/messages';
import { BaseHttpReqRespService, BaseHttpService } from '@myapp/modules/fwcore/services';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import urlJoin from 'url-join';

import { ICompany, IUser, License, Module } from '../models';
import {
    RequestLicense, RequestLicenseSet, RequestModule, RequestRole, RequestRoleSet, RequestRoleTemplate,
    RequestRoleTemplateSet, RequestUserAccessEmailSet, RequestUserImportSet, RequestUserState, RequestUserStateSet,
    ResponseLicense, ResponseLicenseSet, ResponseModule, ResponseRole, ResponseRoleSet, ResponseRoleTemplate,
  ResponseRoleTemplateSet, ResponseUserAccessEmailSet, ResponseUserImportSet, ResponseUserState, ResponseUserStateSet, RequestModuleUserSet
} from '../models/messages';


@Injectable({
  providedIn: 'root'
})

export class AdminService extends BaseHttpReqRespService {

  /*-------------------------- Internal properties  ---------------------------*/

  readonly usersEndpoint: string = 'users';
  readonly companiesEndpoint: string = 'companies';
  readonly modulesEndpoint: string = 'modules';
  readonly LicenseModulesEndpoint: string = 'modules/license-modules';
  readonly userModulesEndpoint: string = 'modules/user-modules';
  readonly licensesEndpoint: string = 'licenses';
  readonly rolesEndpoint: string = 'roles';
  readonly roleTemplatesEndpoint: string = 'roletemplates';
  readonly userImportEndpoint: string = 'users/import';
  readonly userStateEndpoint: string = 'users/state';
  readonly userAccessEmailEndpoint: string = 'users/access-email';

  /*-------------------------- Constructor  --------------------------*/

  constructor(http: HttpClient
    , @Inject(OKTA_AUTH) protected auth: OktaAuth
    , @Inject('CONFIG_SETTINGS') private config: any) {

    super(http, auth);

    const configSettings = config['general'];
    this.baseUrl = configSettings['adminApiUrl'];
    this.apiKey = configSettings['apiKey'];
  }

  /*-------------------------- public properties / methods --------------------*/

  getAllUsers(): Observable<IUser[]> {

    var token = this.getToken();
    if (token != null) {
      const url = urlJoin(this.baseUrl, this.usersEndpoint);
      return this.getItems<IUser>(url, this.getHttpBearerHeaders(token));
    }
    else {
      var emptyUsers: IUser[] = [];
      return of(emptyUsers);
    }

  }

  removeUser(user: IUser): Observable<boolean> {
    var token = this.getToken();
    if (token != null) {
      const url = urlJoin(this.baseUrl, this.usersEndpoint);
      return this.removeItem(user, url, this.getHttpBearerHeaders(token));
    }
    else {
      return of(false);
    }
  }

  saveUser(user: IUser, isNew: boolean): Observable<boolean> {
    var token = this.getToken();
    if (token != null) {
      const url = urlJoin(this.baseUrl, this.usersEndpoint);
      return this.saveItem(user, isNew, url, this.getHttpBearerHeaders(token));
    }
    else {
      return of(false);
    }
  }

  setUserImport$(req: RequestUserImportSet): Observable<ResponseUserImportSet> {

    let emptyResp: ResponseUserImportSet = new ResponseUserImportSet();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.userImportEndpoint);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
      };

      return this.http.post<ResponseUserImportSet>(url, req, httpOptions);

    }
    else {
      return of(emptyResp);
    }
  }

  getUserState$(req: RequestUserState): Observable<ResponseUserState> {
    let emptyList: ResponseUserState = new ResponseUserState();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.userStateEndpoint);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
        , params: fwHttpClientUtils.toHttpParams(req)
      };

      return this.http.get<ResponseUserState>(url, httpOptions);

    }
    else {
      return of(emptyList);
    }
  }

  setUserState$(req: RequestUserStateSet): Observable<ResponseUserStateSet> {

    let emptyResp: ResponseUserStateSet = new ResponseUserStateSet();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.userStateEndpoint);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
      };

      return this.http.post<ResponseUserStateSet>(url, req, httpOptions);

    }
    else {
      return of(emptyResp);
    }
  }

  setUserAccessEmail$(req: RequestUserAccessEmailSet): Observable<ResponseUserAccessEmailSet> {

    let emptyResp: ResponseUserAccessEmailSet = new ResponseUserAccessEmailSet();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.userAccessEmailEndpoint);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
      };

      return this.http.post<ResponseUserAccessEmailSet>(url, req, httpOptions);

    }
    else {
      return of(emptyResp);
    }
  }

  getAllCompanies(): Observable<ICompany[]> {

    var token = this.getToken();
    if (token != null) {
      const url = urlJoin(this.baseUrl, this.companiesEndpoint);
      return this.getItems<ICompany>(url, this.getHttpBearerHeaders(token));
    }
    else {
      var emptyCompanies: ICompany[] = [];
      return of(emptyCompanies);
    }

  }

  getAllModules(ReqModule: RequestModule): Observable<Module[] | any> {
    var token = this.getToken();
    if (token != null) {
      const url = urlJoin(this.baseUrl, this.modulesEndpoint);
      return this.getItems<Module>(url, this.getHttpBearerHeaders(token));
    }
    else {
      var emptyModules: Module[] = [];
      return of(emptyModules);
    }
  }

  getAllLicenseModules(ReqModule: string|any): Observable<Module[] | any> {
    var token = this.getToken();
    if (token != null) {
      const url = urlJoin(this.baseUrl, this.LicenseModulesEndpoint, '?licenseId=' + ReqModule);
      return this.getItems<Module>(url, this.getHttpBearerHeaders(token));
    }
    else {
      var emptyModules: Module[] = [];
      return of(emptyModules);
    }
  }

  getAllUserModules(email: string): Observable<Module[] | any> {
    var token = this.getToken();
    if (token != null) {
      const url = urlJoin(this.baseUrl, this.userModulesEndpoint, '?email='+email);
      return this.getItems<Module>(url, this.getHttpBearerHeaders(token));
    }
    else {
      var emptyModules: Module[] = [];
      return of(emptyModules);
    }
  }


  saveLicenseUserModules(ReqModuleUser: RequestModuleUserSet, isNew: boolean): Observable<Module[] | any> {
    var token = this.getToken();
    if (token != null) {
      const url = urlJoin(this.baseUrl, this.userModulesEndpoint);
      return this.saveItem(ReqModuleUser, isNew, url, this.getHttpBearerHeaders(token));
    }
    else {
      return of(false);
    }
  }

  removeCompany(company: ICompany): Observable<boolean> {
    var token = this.getToken();
    if (token != null) {
      const url = urlJoin(this.baseUrl, this.companiesEndpoint);
      return this.removeItem(company, url, this.getHttpBearerHeaders(token));
    }
    else {
      return of(false);
    }
  }

  saveCompany(company: ICompany, isNew: boolean): Observable<boolean> {
    var token = this.getToken();
    if (token != null) {
      const url = urlJoin(this.baseUrl, this.companiesEndpoint);
      return this.saveItem(company, isNew, url, this.getHttpBearerHeaders(token));
    }
    else {
      return of(false);
    }
  }


  removeModule(module: Module): Observable<boolean> {
    var token = this.getToken();
    if (token != null) {
      const url = urlJoin(this.baseUrl, this.modulesEndpoint);
      return this.removeItem(module, url, this.getHttpBearerHeaders(token));
    }
    else {
      return of(false);
    }
  }

  saveModule(module: Module, isNew: boolean): Observable<boolean> {
    var token = this.getToken();
    if (token != null) {
      const url = urlJoin(this.baseUrl, this.modulesEndpoint);
      return this.saveItem(module, isNew, url, this.getHttpBearerHeaders(token));
    }
    else {
      return of(false);
    }
  }


  saveLicense(license: License, isNew: boolean): Observable<boolean> {
    var token = this.getToken();
    if (token != null) {
      const url = urlJoin(this.baseUrl, this.licensesEndpoint);
      return this.saveItem(license, isNew, url, this.getHttpBearerHeaders(token));
    }
    else {
      return of(false);
    }
  }

  updateLicense$(license: License, isNew: boolean): Observable<License | null> {

    let item: License | null = null;

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.licensesEndpoint);

      let req: RequestLicenseSet = new RequestLicenseSet();
      req.data = [];
      req.data.push(license);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
      };

      return this.http.post<ResponseLicenseSet>(url, req, httpOptions).pipe(
        map(resp => {
          item = (resp.data && resp.data.length == 1) ? resp.data[0] : null;
          return item;
        })
      );
    }
    else {
      return of(item);
    }

  }

  getLicenseUsers(id: string): Observable<string[]> {

    var token = this.getToken();
    if (token != null) {
      const url = urlJoin(this.baseUrl, this.licensesEndpoint + '/' + id + '/users');
      return this.getItems<string>(url, this.getHttpBearerHeaders(token));
    }
    else {
      var emptyUsers: string[] = [];
      return of(emptyUsers);
    }

  }

  /*--------------------------- standardized implementations --------------------------*/

  getModules$(req: RequestModule): Observable<ResponseModule> {

    let emptyList: ResponseModule = new ResponseModule();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.modulesEndpoint);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
        , params: fwHttpClientUtils.toHttpParams(req)
      };

      return this.http.get<ResponseModule>(url, httpOptions);

    }
    else {
      return of(emptyList);
    }

  }


  getLicense$(req: RequestLicense): Observable<ResponseLicense> {

    let emptyList: ResponseLicense = new ResponseLicense();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.licensesEndpoint);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
        , params: fwHttpClientUtils.toHttpParams(req)
      };

      return this.http.get<ResponseLicense>(url, httpOptions);

    }
    else {
      return of(emptyList);
    }

  }

  setLicense$(req: RequestLicenseSet): Observable<ResponseLicenseSet> {

    let emptyList: ResponseLicenseSet = new ResponseLicenseSet();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.licensesEndpoint);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
      };

      return this.http.post<ResponseLicenseSet>(url, req, httpOptions);

    }
    else {
      return of(emptyList);
    }
  }

  removeLicense$(license: License): Observable<boolean> {
    var token = this.getToken();
    if (token != null) {
      const url = urlJoin(this.baseUrl, this.licensesEndpoint);
      return this.removeItem(license, url, this.getHttpBearerHeaders(token));
    }
    else {
      return of(false);
    }
  }


  getRole$(req: RequestRole): Observable<ResponseRole> {

    let emptyResp: ResponseRole = new ResponseRole();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.rolesEndpoint);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
        , params: fwHttpClientUtils.toHttpParams(req)
      };

      return this.http.get<ResponseRole>(url, httpOptions);

    }
    else {
      return of(emptyResp);
    }

  }

  AddUpdate$(req: RequestRoleSet): Observable<ResponseRoleSet> {

    const isRoleExist = req.data?.some(item => (item)?.id ?? false) ?? false;  // Check if data is to add or update
    if (isRoleExist) {
      return this.updateRole$(req);
    }
    else {
      return this.setRole$(req);
    }
  }

  setRole$(req: RequestRoleSet): Observable<ResponseRoleSet> {

    let emptyResp: ResponseRoleSet = new ResponseRoleSet();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.rolesEndpoint);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
      };

      return this.http.post<ResponseRoleSet>(url, req, httpOptions);

    }
    else {
      return of(emptyResp);
    }
  }

  updateRole$(req: RequestRoleSet): Observable<ResponseRoleSet> {

    let emptyResp: ResponseRoleSet = new ResponseRoleSet();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.rolesEndpoint);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
      };

      return this.http.put<ResponseRoleSet>(url, req, httpOptions);

    }
    else {
      return of(emptyResp);
    }
  }

  removeRole$(req: RequestDelete): Observable<ResponseDelete> {

    let emptyResp: ResponseDelete = new ResponseDelete();
    emptyResp.success = false;

    var token = this.getToken();

    if (token != null) {
      let url = urlJoin(this.baseUrl, this.rolesEndpoint);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
        , params: fwHttpClientUtils.toHttpParams(req)
      };

      return this.http.delete<ResponseDelete>(url, httpOptions);

    }
    else {
      return of(emptyResp);
    }
  }


  getRoleTemplate$(req: RequestRoleTemplate): Observable<ResponseRoleTemplate> {

    let emptyResp: ResponseRoleTemplate = new ResponseRoleTemplate();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.roleTemplatesEndpoint);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
        , params: fwHttpClientUtils.toHttpParams(req)
      };

      return this.http.get<ResponseRoleTemplate>(url, httpOptions);

    }
    else {
      return of(emptyResp);
    }

  }

  setRoleTemplate$(req: RequestRoleTemplateSet): Observable<ResponseRoleTemplateSet> {

    let emptyResp: ResponseRoleTemplateSet = new ResponseRoleTemplateSet();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.roleTemplatesEndpoint);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
      };

      return this.http.post<ResponseRoleTemplateSet>(url, req, httpOptions);

    }
    else {
      return of(emptyResp);
    }
  }

  removeRoleTemplate$(req: RequestDelete): Observable<ResponseDelete> {

    let emptyResp: ResponseDelete = new ResponseDelete();
    emptyResp.success = false;

    var token = this.getToken();

    if (token != null) {
      let url = urlJoin(this.baseUrl, this.roleTemplatesEndpoint);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
        , params: fwHttpClientUtils.toHttpParams(req)
      };

      return this.http.delete<ResponseDelete>(url, httpOptions);

    }
    else {
      return of(emptyResp);
    }
  }

}
